/**
 * Secret Chat SignalR client wrapper.
 *
 * Thin facade around `@microsoft/signalr.HubConnection` that exposes a
 * stable, framework-agnostic interface to the rest of the SPA:
 *   - `connect/disconnect` lifecycle methods
 *   - `state` snapshot + `onConnectionStateChange` observable
 *   - `onNewMessage / onReadReceiptChanged` event subscriptions
 *
 * Design contract (`design.md` §Frontend Layer › signalRClient,
 * §Event Contract):
 *   - Connection state enum is `idle | connecting | connected | reconnecting | failed`
 *   - Hub URL + access token are supplied by the caller (the negotiate
 *     handshake lives in `chatApi.ts`); SignalR will re-invoke the
 *     supplied token factory on every reconnect, so we capture the
 *     token at connect time.
 *   - Reconnect policy is the documented `[0, 2000, 10000, 30000]` ms
 *     retry sequence (4 attempts then onclose -> `failed`).
 *   - Event target names (`newMessage`, `readReceiptChanged`) match the
 *     server-side SignalR Output Binding payloads emitted by
 *     `api/src/functions/{sendMessage,markRead}.ts`.
 *
 * Dependency direction (§File Structure Plan):
 *   - This module sits in the Frontend Layer and depends only on the
 *     external `@microsoft/signalr` library. Hooks (`useSignalRConnection`,
 *     `useMessages`) consume the `SignalRClient` interface; no upstream
 *     coupling to React or to the REST client.
 *
 * Testability:
 *   - `createSignalRClient` accepts a `builderFactory` so unit tests
 *     can swap in a fake `HubConnectionBuilder` without touching the
 *     network. Production code uses the default factory and lets the
 *     real builder negotiate WebSocket transport.
 */

import { HubConnectionBuilder } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Observable connection lifecycle states.
 *
 * Mapped from the underlying `HubConnection` callbacks:
 *   - `idle`         — pre-connect or after a clean `disconnect()`.
 *   - `connecting`   — `start()` invoked, awaiting resolution.
 *   - `connected`    — `start()` resolved (initial connect or `onreconnected`).
 *   - `reconnecting` — `onreconnecting` fired due to a transient drop.
 *   - `failed`       — `onclose` fired (max retries exceeded or unrecoverable).
 */
export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

/**
 * Wire shape of a chat message. Kept in lockstep with the same-named
 * type in `chatApi.ts`. We do *not* re-export from there to avoid a
 * circular-feeling dependency between the REST and realtime layers —
 * both are sibling consumers of the server contract.
 */
export interface Message {
  readonly id: string;
  readonly roomId: string;
  readonly seq: number;
  readonly senderNickname: string;
  readonly content: string;
  readonly sentAt: string;
}

/** Payload for the SignalR `readReceiptChanged` event. */
export interface ReadReceiptEvent {
  readonly nickname: string;
  readonly lastReadSeq: number;
  readonly updatedAt: string;
}

/** Payload for the SignalR `announcementChanged` event. */
export interface AnnouncementEvent {
  readonly roomId: string;
  readonly announcement: string;
}

/**
 * Payload for the SignalR `readReceiptSettingChanged` event.
 *
 * Emitted when a participant toggles their own `showReadReceipt` preference
 * via the Dashboard settings dialog. The receiver hides own-message
 * "既読/未読" indicators when the *other* participant's value is `false`.
 */
export interface ReadReceiptSettingEvent {
  readonly roomId: string;
  readonly nickname: string;
  readonly showReadReceipt: boolean;
}

/**
 * Payload for the SignalR `guestMessageLimitChanged` event.
 *
 * Emitted when the room owner changes the configured guest message limit
 * via the Dashboard settings dialog. The counter is also reset, so the
 * payload carries the post-reset value (`0`).
 */
export interface GuestMessageLimitEvent {
  readonly roomId: string;
  readonly guestMessageLimit: number;
  readonly consecutiveGuestMessages: number;
}

/**
 * Payload for the SignalR `templatesChanged` event.
 *
 * オーナーがダッシュボードの設定モーダルからテンプレート一覧を更新したとき
 * に発火する。受信側はそのまま現在のテンプレート状態を上書きする。
 */
export interface TemplatesEvent {
  readonly roomId: string;
  readonly templates: readonly string[];
}

/**
 * Connect-time parameters returned by `POST /api/negotiate`. Pre-bundled
 * so callers don't accidentally pass them in the wrong order.
 */
export interface ConnectNegotiate {
  readonly url: string;
  readonly accessToken: string;
}

/**
 * Public surface for consumers. Hooks bind to this interface (not the
 * concrete factory return type) so they can be unit-tested with a fake
 * client and so production code can swap the implementation without
 * touching the consumers.
 */
export interface SignalRClient {
  /**
   * Start a new connection using the negotiate response. Calling
   * `connect` again while already `connecting` or `connected` is a
   * no-op: the second call returns the in-flight (or already-resolved)
   * promise so callers don't have to coordinate.
   */
  connect(negotiate: ConnectNegotiate): Promise<void>;

  /** Stop the active connection and reset state to `idle`. */
  disconnect(): Promise<void>;

  /** Current connection state. Read-only snapshot. */
  readonly state: ConnectionState;

  /**
   * Subscribe to state-change notifications. Returns an unsubscribe
   * function — callers (hooks, components) MUST call it on cleanup to
   * avoid leaks.
   */
  onConnectionStateChange(handler: (state: ConnectionState) => void): () => void;

  /** Subscribe to `newMessage` hub events. Returns an unsubscribe function. */
  onNewMessage(handler: (message: Message) => void): () => void;

  /** Subscribe to `readReceiptChanged` hub events. Returns an unsubscribe function. */
  onReadReceiptChanged(handler: (event: ReadReceiptEvent) => void): () => void;

  /** Subscribe to `announcementChanged` hub events. Returns an unsubscribe function. */
  onAnnouncementChanged(handler: (event: AnnouncementEvent) => void): () => void;

  /** Subscribe to `readReceiptSettingChanged` hub events. Returns an unsubscribe function. */
  onReadReceiptSettingChanged(
    handler: (event: ReadReceiptSettingEvent) => void,
  ): () => void;

  /** Subscribe to `guestMessageLimitChanged` hub events. Returns an unsubscribe function. */
  onGuestMessageLimitChanged(
    handler: (event: GuestMessageLimitEvent) => void,
  ): () => void;

  /** Subscribe to `templatesChanged` hub events. Returns an unsubscribe function. */
  onTemplatesChanged(handler: (event: TemplatesEvent) => void): () => void;
}

/** Factory options. All fields are optional; production callers pass none. */
export interface CreateSignalRClientOptions {
  /**
   * DI seam for tests. Production code lets this default to
   * `() => new HubConnectionBuilder()`. Tests inject a fake builder
   * that records configuration calls and produces a fake connection.
   */
  readonly builderFactory?: () => HubConnectionBuilder;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Automatic reconnect retry delays in milliseconds.
 *
 * Sourced from `design.md` §Technology Stack and `research.md`:
 * `[0, 2000, 10000, 30000]` — four attempts (immediate, +2s, +10s, +30s)
 * before SignalR gives up and fires `onclose`, at which point we
 * transition to `failed` and let the UI prompt for manual retry.
 */
const RECONNECT_DELAYS_MS: readonly number[] = [0, 2000, 10000, 30000];

/** SignalR event target names — must match the server's Output Binding. */
const EVENT_NEW_MESSAGE = 'newMessage';
const EVENT_READ_RECEIPT_CHANGED = 'readReceiptChanged';
const EVENT_ANNOUNCEMENT_CHANGED = 'announcementChanged';
const EVENT_READ_RECEIPT_SETTING_CHANGED = 'readReceiptSettingChanged';
const EVENT_GUEST_MESSAGE_LIMIT_CHANGED = 'guestMessageLimitChanged';
const EVENT_TEMPLATES_CHANGED = 'templatesChanged';

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Build a `SignalRClient`. The returned object owns at most one
 * `HubConnection` at a time; calling `disconnect()` discards the
 * underlying connection so the next `connect()` constructs a fresh one
 * (the real `HubConnectionBuilder` doesn't support reusing a stopped
 * connection cleanly across all transports, so we always rebuild).
 *
 * Why a factory (not a class)?
 *   - Tests inject `builderFactory` without subclassing.
 *   - The closure-based state model keeps mutation centralized and
 *     prevents external code from poking at internals.
 */
export function createSignalRClient(
  options: CreateSignalRClientOptions = {},
): SignalRClient {
  const builderFactory =
    options.builderFactory ?? (() => new HubConnectionBuilder());

  // -- Mutable state (encapsulated; no external mutation path) ------------
  let state: ConnectionState = 'idle';
  let connection: HubConnection | null = null;
  // Once `connect()` is invoked we cache its promise so a concurrent
  // re-entry can `await` the same resolution. Cleared in `disconnect()`.
  let connectPromise: Promise<void> | null = null;

  // Listener registries. Sets so duplicate registrations are deduped and
  // unsubscribe is O(1). We snapshot to an array before fan-out so a
  // handler unsubscribing during iteration doesn't skip its neighbour.
  const stateListeners = new Set<(s: ConnectionState) => void>();
  const newMessageListeners = new Set<(m: Message) => void>();
  const readReceiptListeners = new Set<(e: ReadReceiptEvent) => void>();
  const announcementListeners = new Set<(e: AnnouncementEvent) => void>();
  const readReceiptSettingListeners = new Set<(e: ReadReceiptSettingEvent) => void>();
  const guestMessageLimitListeners = new Set<(e: GuestMessageLimitEvent) => void>();
  const templatesListeners = new Set<(e: TemplatesEvent) => void>();

  /** Centralized state mutation + fan-out so transitions stay observable. */
  function setState(next: ConnectionState): void {
    if (state === next) return;
    state = next;
    for (const fn of [...stateListeners]) fn(next);
  }

  // -- connect ------------------------------------------------------------
  function connect(negotiate: ConnectNegotiate): Promise<void> {
    // Concurrent / repeated connect is a no-op. The first promise wins;
    // later callers `await` the same resolution. This guarantees that
    // `start()` is invoked at most once per connection lifecycle.
    if (connectPromise !== null) return connectPromise;

    setState('connecting');

    const builder = builderFactory()
      .withUrl(negotiate.url, {
        // SignalR will re-invoke this factory on every reconnect, so it
        // must remain stable for the lifetime of the connection. We
        // close over the token captured at connect time.
        accessTokenFactory: () => negotiate.accessToken,
      })
      .withAutomaticReconnect([...RECONNECT_DELAYS_MS]);

    const conn = builder.build();
    connection = conn;

    // Register hub-side event handlers up front. Doing this before
    // `start()` ensures we don't lose early messages emitted as soon
    // as the connection negotiates.
    conn.on(EVENT_NEW_MESSAGE, (payload: Message) => {
      for (const fn of [...newMessageListeners]) fn(payload);
    });
    conn.on(EVENT_READ_RECEIPT_CHANGED, (payload: ReadReceiptEvent) => {
      for (const fn of [...readReceiptListeners]) fn(payload);
    });
    conn.on(EVENT_ANNOUNCEMENT_CHANGED, (payload: AnnouncementEvent) => {
      for (const fn of [...announcementListeners]) fn(payload);
    });
    conn.on(
      EVENT_READ_RECEIPT_SETTING_CHANGED,
      (payload: ReadReceiptSettingEvent) => {
        for (const fn of [...readReceiptSettingListeners]) fn(payload);
      },
    );
    conn.on(
      EVENT_GUEST_MESSAGE_LIMIT_CHANGED,
      (payload: GuestMessageLimitEvent) => {
        for (const fn of [...guestMessageLimitListeners]) fn(payload);
      },
    );
    conn.on(EVENT_TEMPLATES_CHANGED, (payload: TemplatesEvent) => {
      for (const fn of [...templatesListeners]) fn(payload);
    });

    // Lifecycle hooks. `onreconnecting` fires when the underlying
    // transport drops; `onreconnected` fires once the policy succeeds;
    // `onclose` fires only when the policy has exhausted (or the user
    // called `stop()` — see `disconnect()` for that path).
    conn.onreconnecting(() => setState('reconnecting'));
    conn.onreconnected(() => setState('connected'));
    conn.onclose(() => {
      // `onclose` after a user-initiated `disconnect()` would normally
      // be a redundant signal; `disconnect()` clears `connection` first
      // so we still observe the close but never overwrite `idle` back
      // to `failed`. Guard explicitly: if the connection has been
      // detached, treat the close as expected.
      if (connection !== conn) return;
      // Clear the cached promise + connection so a subsequent `connect()`
      // (e.g. the user clicking the retry button in `ConnectionStatus`)
      // rebuilds a fresh HubConnection rather than silently re-awaiting
      // an already-resolved promise on a dead transport. (See tasks.md
      // Implementation Notes 6.5.)
      connection = null;
      connectPromise = null;
      setState('failed');
    });

    connectPromise = conn
      .start()
      .then(() => {
        setState('connected');
      })
      .catch((err: unknown) => {
        // start() failed before any onclose can fire. Surface the
        // failure to consumers and clear the in-flight promise so a
        // retry path (manual `connect()` again) is unblocked.
        setState('failed');
        connectPromise = null;
        connection = null;
        throw err;
      });

    return connectPromise;
  }

  // -- disconnect ---------------------------------------------------------
  async function disconnect(): Promise<void> {
    const conn = connection;
    if (conn === null) {
      // Already idle / never connected. Still reset state defensively in
      // case a prior `failed` snapshot lingers; the contract says
      // `disconnect()` ends in `idle`.
      setState('idle');
      return;
    }

    // Detach before stopping so the `onclose` we triggered ourselves
    // doesn't get mistaken for a `failed` transition.
    connection = null;
    connectPromise = null;
    try {
      await conn.stop();
    } finally {
      setState('idle');
    }
  }

  // -- Subscription helpers (shared shape) --------------------------------
  function subscribe<T>(set: Set<T>, handler: T): () => void {
    set.add(handler);
    return () => {
      set.delete(handler);
    };
  }

  return Object.freeze<SignalRClient>({
    connect,
    disconnect,
    get state() {
      return state;
    },
    onConnectionStateChange(handler) {
      return subscribe(stateListeners, handler);
    },
    onNewMessage(handler) {
      return subscribe(newMessageListeners, handler);
    },
    onReadReceiptChanged(handler) {
      return subscribe(readReceiptListeners, handler);
    },
    onAnnouncementChanged(handler) {
      return subscribe(announcementListeners, handler);
    },
    onReadReceiptSettingChanged(handler) {
      return subscribe(readReceiptSettingListeners, handler);
    },
    onGuestMessageLimitChanged(handler) {
      return subscribe(guestMessageLimitListeners, handler);
    },
    onTemplatesChanged(handler) {
      return subscribe(templatesListeners, handler);
    },
  });
}
